import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const safeParse = (value, fallback) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const profilePhotoKey = (user) => {
  const accountKey = user?.email || user?.id;

  return accountKey
    ? `mlb_profile_photo:${accountKey}`
    : null;
};

const readLocalState = (user) => {
  if (typeof window === 'undefined') return {};

  const photoKey = profilePhotoKey(user);

  return {
    uploadHistory: safeParse(
      window.localStorage.getItem('bulk_upload_history'),
      []
    ),

    calendarNotes: safeParse(
      window.localStorage.getItem('mlb_calendar_notes'),
      {}
    ),

    profilePhoto: photoKey
      ? window.localStorage.getItem(photoKey)
      : null,

    theme:
      window.localStorage.getItem('theme') || null,

    prohibitedWords:
      window.localStorage.getItem('ml_prohibited_words') || null,

    translationMode:
      window.localStorage.getItem('ml_translation_mode') || null,
  };
};

const setOrRemove = (key, value) => {
  if (value === null || value === undefined) {
    window.localStorage.removeItem(key);
  } else {
    window.localStorage.setItem(key, String(value));
  }
};

const applyLocalState = (user, state = {}) => {
  if (typeof window === 'undefined') return;

  if ('uploadHistory' in state) {
    window.localStorage.setItem(
      'bulk_upload_history',
      JSON.stringify(
        Array.isArray(state.uploadHistory)
          ? state.uploadHistory
          : []
      )
    );
  }

  if ('calendarNotes' in state) {
    window.localStorage.setItem(
      'mlb_calendar_notes',
      JSON.stringify(
        state.calendarNotes &&
        typeof state.calendarNotes === 'object'
          ? state.calendarNotes
          : {}
      )
    );
  }

  const photoKey = profilePhotoKey(user);

  if (photoKey && 'profilePhoto' in state) {
    setOrRemove(photoKey, state.profilePhoto);
  }

  if ('theme' in state) {
    setOrRemove('theme', state.theme);

    const root = document.documentElement;

    if (state.theme === 'dark') {
      root.classList.add('dark');
    } else if (state.theme === 'light') {
      root.classList.remove('dark');
    }
  }

  if ('prohibitedWords' in state) {
    setOrRemove(
      'ml_prohibited_words',
      state.prohibitedWords
    );
  }

  if ('translationMode' in state) {
    setOrRemove(
      'ml_translation_mode',
      state.translationMode
    );
  }
};

const sameValue = (a, b) =>
  JSON.stringify(a) === JSON.stringify(b);

const UserAppStateSync = () => {
  const { user } = useAuth();

  const lastLocalStateRef = useRef(null);
  const lastRemoteUpdatedAtRef = useRef(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      lastLocalStateRef.current = null;
      lastRemoteUpdatedAtRef.current = null;
      return undefined;
    }

    let stopped = false;

    const reloadIfNeeded = (
      before,
      after,
      remoteUpdatedAt
    ) => {
      if (sameValue(before, after)) {
        return;
      }

      const marker =
        `mercadobulk_cloud_restore:` +
        `${user.id}:` +
        `${remoteUpdatedAt || 'initial'}`;

      try {
        if (
          window.sessionStorage.getItem(marker) === '1'
        ) {
          return;
        }

        window.sessionStorage.setItem(marker, '1');
      } catch {}

      setTimeout(() => {
        if (!stopped) {
          (
  window.__MERCADOBULK_PUBLISH_ACTIVE__ === true ||
  (window.location.pathname.startsWith('/bulk-upload') &&
  window.__MERCADOBULK_UPLOAD_RUNTIME__?.currentStep === 'publish')
    ? undefined
    : window.location.reload()
);
        }
      }, 120);
    };

    const fetchRemote = async () => {
      const { data, error } = await supabase
        .from('user_app_state')
        .select('state,updated_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data || null;
    };

    const writeRemote = async (state) => {
      const updatedAt = new Date().toISOString();

      const { error } = await supabase
        .from('user_app_state')
        .upsert(
          {
            user_id: user.id,
            state,
            updated_at: updatedAt
          },
          {
            onConflict: 'user_id'
          }
        );

      if (error) {
        throw error;
      }

      lastRemoteUpdatedAtRef.current = updatedAt;

      return updatedAt;
    };

    const initialSync = async () => {
      try {
        const localState = readLocalState(user);
        const remote = await fetchRemote();

        if (stopped) return;

        /*
         * Primera migración:
         * si todavía no existe estado remoto,
         * SUBIMOS lo que ya existe en este navegador.
         *
         * Así no reemplazamos tu historial/foto por vacío.
         */
        if (!remote) {
          await writeRemote(localState);

          lastLocalStateRef.current =
            readLocalState(user);

          console.log(
            '☁️ Estado local inicial guardado en Supabase'
          );

          return;
        }

        const remoteState =
          remote?.state &&
          typeof remote.state === 'object'
            ? remote.state
            : {};

        /*
         * Si en el futuro agregamos nuevos campos:
         * conservar local cuando el remoto todavía no
         * conozca el campo.
         *
         * En campos existentes manda la nube.
         */
        const mergedState = {
          ...localState,
          ...remoteState
        };

        const before = readLocalState(user);

        applyLocalState(user, mergedState);

        const after = readLocalState(user);

        lastLocalStateRef.current = after;
        lastRemoteUpdatedAtRef.current =
          remote?.updated_at || null;

        /*
         * Si este navegador aportó alguna propiedad nueva
         * que todavía no existía remotamente, consolidarla.
         */
        if (!sameValue(remoteState, mergedState)) {
          await writeRemote(mergedState);
        }

        console.log(
          '☁️ Estado de cuenta restaurado desde Supabase'
        );

        reloadIfNeeded(
          before,
          after,
          remote?.updated_at
        );
      } catch (error) {
        console.error(
          'Error sincronizando estado de cuenta:',
          error?.message || error
        );

        lastLocalStateRef.current =
          readLocalState(user);
      }
    };

    const pushLocalChanges = async () => {
      if (syncingRef.current || stopped) return;

      const current = readLocalState(user);
      const previous =
        lastLocalStateRef.current || current;

      const changedKeys = Object.keys(current).filter(
        (key) =>
          !sameValue(
            current?.[key],
            previous?.[key]
          )
      );

      if (changedKeys.length === 0) {
        return;
      }

      syncingRef.current = true;

      try {
        const remote = await fetchRemote();

        const remoteState =
          remote?.state &&
          typeof remote.state === 'object'
            ? remote.state
            : {};

        const nextRemote = {
          ...remoteState
        };

        /*
         * Solo escribir en nube los campos que realmente
         * cambiaron localmente.
         *
         * Así un cambio de calendario no pisa el historial
         * guardado desde otro dispositivo.
         */
        changedKeys.forEach((key) => {
          nextRemote[key] = current[key];
        });

        await writeRemote(nextRemote);

        lastLocalStateRef.current = current;

        console.log(
          '☁️ Estado sincronizado:',
          changedKeys.join(', ')
        );
      } catch (error) {
        console.error(
          'No se pudo subir el estado de cuenta:',
          error?.message || error
        );
      } finally {
        syncingRef.current = false;
      }
    };

    const pullRemoteChanges = async () => {
      if (syncingRef.current || stopped) return;

      /*
       * Antes de bajar cambios de otro equipo,
       * subir cualquier modificación local pendiente.
       */
      await pushLocalChanges();

      try {
        // Consultar solo la fecha, no todo el estado.
        const { data: remoteVersion, error: versionError } =
          await supabase
            .from('user_app_state')
            .select('updated_at')
            .eq('user_id', user.id)
            .maybeSingle();

        if (versionError) {
          throw versionError;
        }

        if (
          stopped ||
          !remoteVersion ||
          remoteVersion.updated_at ===
            lastRemoteUpdatedAtRef.current
        ) {
          return;
        }

        // Descargar el estado solo si hubo cambios.
        const remote = await fetchRemote();

        if (
          !remote ||
          remote?.updated_at ===
            lastRemoteUpdatedAtRef.current
        ) {
          return;
        }

        const remoteState =
          remote?.state &&
          typeof remote.state === 'object'
            ? remote.state
            : {};

        const before = readLocalState(user);

        const merged = {
          ...before,
          ...remoteState
        };

        applyLocalState(user, merged);

        const after = readLocalState(user);

        lastLocalStateRef.current = after;
        lastRemoteUpdatedAtRef.current =
          remote?.updated_at || null;

        reloadIfNeeded(
          before,
          after,
          remote?.updated_at
        );
      } catch (error) {
        console.error(
          'No se pudo descargar el estado de cuenta:',
          error?.message || error
        );
      }
    };

    void initialSync();

    /*
     * Los módulos existentes siguen escribiendo
     * exactamente al mismo localStorage.
     *
     * Cada 2 segundos detectamos únicamente cambios.
     */
    const uploadTimer = window.setInterval(
      () => {
        void pushLocalChanges();
      },
      2000
    );

    /*
     * Revisar cambios hechos desde otro dispositivo.
     */
    const remoteTimer = window.setInterval(
      () => {
        void pullRemoteChanges();
      },
      15000
    );

    const handleFocus = () => {
      void pullRemoteChanges();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void pullRemoteChanges();
      }
    };

    window.addEventListener('focus', handleFocus);

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      stopped = true;

      window.clearInterval(uploadTimer);
      window.clearInterval(remoteTimer);

      window.removeEventListener(
        'focus',
        handleFocus
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [user?.id, user?.email]);

  return null;
};

export default UserAppStateSync;
